import { Link, useNavigate, useLocation } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { useState, useEffect } from "react";
import { useTheme } from "../contexts/ThemeContext";
import {
  Home, Bell, Users, Menu, Radio, MessageCircle, Sparkles, Tag, Trophy,
  Search, Settings, User, LogOut, Shield, Plane, Gift, TrendingUp, Building, Briefcase, Share2, MapPin, CalendarClock, Award, Globe, Bot, Heart, Calendar, UserRound, Sun, Moon
} from "lucide-react";

// Detect if running as installed PWA
const isPWA = () => {
  return window.matchMedia('(display-mode: standalone)').matches || 
         window.navigator.standalone === true ||
         document.referrer.includes('android-app://');
};

export const GlobalMobileNav = ({ user, notifications, onLogout, children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [isGlobeActive, setIsGlobeActive] = useState(false);

  useEffect(() => {
    setIsStandalone(isPWA());
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    const handler = (e) => setIsStandalone(e.matches);
    mediaQuery.addEventListener('change', handler);
    
    // Watch for globe-active class on body
    const observer = new MutationObserver(() => {
      setIsGlobeActive(document.body.classList.contains('globe-active'));
    });
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    
    return () => { mediaQuery.removeEventListener('change', handler); observer.disconnect(); };
  }, []);

  // Don't show nav on landing, login, register pages
  const hideNavPaths = ['/', '/login', '/register', '/forgot-password', '/reset-password', '/pending-approval', '/terms-of-service'];
  const shouldShowNav = user && !hideNavPaths.includes(location.pathname);

  // Always show nav bar on mobile for authenticated users (not just PWA)
  const showMobileNavBar = shouldShowNav;

  // Hide floating button on certain paths and on the sQawk AI page itself
  const hideFloatingBtn = !user || location.pathname === '/trending' || location.pathname.startsWith('/trending/') || hideNavPaths.includes(location.pathname) || isGlobeActive;

  return (
    <>
      {children}
      
      {/* Floating sQawk AI shortcut — Left side, opposite ChatWidget */}
      {!hideFloatingBtn && (
        <button
          onClick={() => navigate("/trending")}
          className="fixed bottom-[7.5rem] lg:bottom-6 left-3 lg:left-6 z-40 w-9 h-9 lg:w-14 lg:h-14 rounded-full flex items-center justify-center transition-all hover:scale-110 group opacity-80 hover:opacity-100"
          style={{
            background: "linear-gradient(135deg, #00b4ff, #7c4dff)",
            boxShadow: "0 0 16px rgba(0,180,255,0.25), 0 4px 12px rgba(0,0,0,0.2)",
          }}
          title="Open sQawk AI"
          data-testid="sqawk-ai-floating-btn"
        >
          <Bot className="w-4 h-4 lg:w-7 lg:h-7 text-white relative z-10" style={{ filter: "drop-shadow(0 0 6px rgba(0,220,255,0.5))" }} />
        </button>
      )}
      
      {/* Global Mobile Navigation Bar - Shows on all authenticated pages */}
      {showMobileNavBar && (
        <>
          {/* Spacer to prevent content from being hidden behind nav */}
          <div className="lg:hidden" style={{ height: "calc(68px + env(safe-area-inset-bottom, 0px) + 16px)" }} />
          <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-slate-950/95 backdrop-blur-xl border-t border-slate-800 px-2 z-50" style={{ paddingBottom: "max(env(safe-area-inset-bottom, 0px), 8px)", paddingTop: "10px" }} data-testid="global-mobile-nav">
          <div className="flex items-center justify-around">
            <Link to="/dashboard" className={`p-3 transition-colors ${location.pathname === '/dashboard' ? 'text-emerald-400' : 'text-slate-400 hover:text-white'}`}>
              <Home className="w-6 h-6" />
            </Link>
            <Link to="/notifications" className="p-3 text-slate-400 hover:text-white transition-colors relative">
              <Bell className="w-6 h-6" />
              {notifications?.unread_count > 0 && (
                <span className="absolute top-1 right-0.5 w-5 h-5 bg-emerald-500 rounded-full text-[11px] text-white flex items-center justify-center font-medium">
                  {notifications.unread_count > 9 ? '9+' : notifications.unread_count}
                </span>
              )}
            </Link>
            <Link to={`/profile/${user?.username}`} className="-mt-6 transition-transform hover:scale-110 active:scale-95">
              <div className="w-[4.5rem] h-[4.5rem] rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 p-0.5 shadow-lg shadow-cyan-500/30">
                <div className="w-full h-full rounded-full bg-slate-900 flex items-center justify-center overflow-hidden">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_a9254929-916a-4499-88cc-305ef331d975/artifacts/df8ur861_ChatGPT%20Image%20Jan%2027%2C%202026%2C%2008_01_31%20PM.png" 
                    alt="sQawk" 
                    className="w-14 h-14 object-contain rounded-full" 
                  />
                </div>
              </div>
            </Link>
            <Link to="/communities" className={`p-3 transition-colors ${location.pathname.includes('/communities') ? 'text-emerald-400' : 'text-slate-400 hover:text-white'}`}>
              <Users className="w-6 h-6" />
            </Link>
            <button onClick={() => setShowMobileMenu(true)} className="p-3 text-slate-400 hover:text-white transition-colors">
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </nav>
        </>
      )}

      {/* Mobile Menu Dialog */}
      <Dialog open={showMobileMenu} onOpenChange={setShowMobileMenu}>
        <DialogContent 
          className={`backdrop-blur-xl max-w-sm max-h-[80vh] overflow-y-auto ${
            theme === 'light' 
              ? 'bg-white border-slate-200' 
              : 'bg-slate-900/98 border-slate-700/50'
          }`} 
          style={theme === 'dark' ? {background: 'linear-gradient(135deg, #0c1020 0%, #141a30 25%, #0e1428 50%, #161d35 75%, #0c1020 100%)'} : {}}
          data-menu-theme={theme}
        >
          {/* Glistening stars (dark mode only) */}
          {theme === 'dark' && (
          <div className="absolute inset-0 pointer-events-none animate-pulse rounded-lg" style={{animationDuration: '3s', background: 'radial-gradient(1px 1px at 15% 25%, rgba(255,255,255,0.7) 0%, transparent 100%), radial-gradient(1px 1px at 45% 15%, rgba(212,212,216,0.6) 0%, transparent 100%), radial-gradient(1.5px 1.5px at 72% 35%, rgba(255,255,255,0.8) 0%, transparent 100%), radial-gradient(1px 1px at 88% 18%, rgba(192,192,192,0.5) 0%, transparent 100%), radial-gradient(1px 1px at 25% 65%, rgba(255,255,255,0.6) 0%, transparent 100%), radial-gradient(1.5px 1.5px at 60% 75%, rgba(212,212,216,0.7) 0%, transparent 100%), radial-gradient(1px 1px at 35% 88%, rgba(255,255,255,0.5) 0%, transparent 100%), radial-gradient(1px 1px at 82% 60%, rgba(192,192,192,0.6) 0%, transparent 100%)'}} />
          )}
          <DialogHeader>
            <DialogTitle className={`text-center ${
              theme === 'light' 
                ? 'text-slate-900 font-bold text-lg' 
                : 'text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 via-blue-400 to-cyan-300 animate-[glow-pulse_2.5s_ease-in-out_infinite] drop-shadow-[0_0_12px_rgba(34,211,238,0.6)]'
            }`} style={theme === 'dark' ? { textShadow: '0 0 20px rgba(34,211,238,0.5), 0 0 40px rgba(59,130,246,0.3)' } : {}}>sQawk Dashboard</DialogTitle>
            <DialogDescription className="sr-only">Navigate to different sections of sQawk</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 mt-4">
            {/* === GO LIVE / MESSAGES / NOTIFICATIONS === */}
            <div className="col-span-2 flex items-center gap-2">
              <Radio className="w-4 h-4 text-emerald-400" />
              <span className={`text-xs font-semibold uppercase tracking-wider ${theme === 'light' ? 'text-emerald-600' : 'text-[#86efac]'}`}>Connect</span>
              <div className="flex-1 h-px bg-gradient-to-r from-green-400/50 to-transparent" />
            </div>
            
            {/* Go Live */}
            <button onClick={() => { setShowMobileMenu(false); navigate("/dashboard?action=go-live"); }}
              className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all col-span-2 ${
                theme === 'light' 
                  ? 'bg-emerald-50 border-emerald-200 hover:border-emerald-400 hover:bg-emerald-100' 
                  : 'bg-[#0a2e14] border-green-500/50 hover:border-green-400 hover:bg-[#0f3b1a] shadow-[0_0_18px_rgba(34,197,94,0.3),inset_0_0_20px_rgba(34,197,94,0.1)]'
              }`}
              data-testid="global-mobile-go-live-btn">
              <div className="flex items-center gap-2">
                <Radio className={`w-6 h-6 animate-pulse ${theme === 'light' ? 'text-emerald-600' : 'text-[#86efac]'}`} />
                <span className={`text-lg font-bold ${theme === 'light' ? 'text-emerald-900' : 'text-white'}`}>Go Live</span>
              </div>
              <span className={`text-xs ${theme === 'light' ? 'text-emerald-600' : 'text-[#86efac]'}`}>Start streaming now</span>
            </button>
            
            {/* Messages */}
            <button onClick={() => { setShowMobileMenu(false); navigate("/messages"); }}
              className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                theme === 'light' 
                  ? 'bg-emerald-50 border-emerald-200 hover:border-emerald-400 hover:bg-emerald-100' 
                  : 'bg-[#0a2415] border-green-500/40 hover:border-green-400 hover:bg-[#0f3019] shadow-[0_0_14px_rgba(34,197,94,0.25)]'
              }`}>
              <MessageCircle className={`w-6 h-6 ${theme === 'light' ? 'text-emerald-600' : 'text-[#86efac]'}`} />
              <span className={`text-sm font-medium ${theme === 'light' ? 'text-emerald-900' : 'text-white'}`}>Messages</span>
            </button>
            
            {/* Notifications */}
            <button onClick={() => { setShowMobileMenu(false); navigate("/notifications"); }}
              className="flex flex-col items-center gap-2 p-4 rounded-xl bg-[#0a2415] border-2 border-green-500/40 hover:border-green-400 hover:bg-[#0f3019] transition-all shadow-[0_0_14px_rgba(34,197,94,0.25),inset_0_0_14px_rgba(34,197,94,0.08)] relative">
              <Bell className="w-6 h-6 text-[#86efac]" />
              <span className="text-white text-sm font-medium">Notifications</span>
            </button>
            
            {/* === COMMUNITIES Section — Light Green === */}
            <div className="col-span-2 flex items-center gap-2 mt-2">
              <Users className="w-4 h-4 text-[#fdba74]" />
              <span className="text-[#fdba74] text-xs font-semibold uppercase tracking-wider">Communities</span>
              <div className="flex-1 h-px bg-gradient-to-r from-orange-400/50 to-transparent" />
            </div>
            
            {/* My Community */}
            <button onClick={() => { setShowMobileMenu(false); navigate("/communities?tab=cities"); }}
              className="flex flex-col items-center gap-2 p-4 rounded-xl bg-[#2e1a0a] border-2 border-orange-500/40 hover:border-orange-400 hover:bg-[#3b220f] transition-all shadow-[0_0_14px_rgba(249,115,22,0.25),inset_0_0_14px_rgba(249,115,22,0.08)]">
              <MapPin className="w-6 h-6 text-[#fdba74]" />
              <span className="text-white text-sm font-medium">My Community</span>
            </button>
            
            {/* sQawk Friends */}
            <button onClick={() => { setShowMobileMenu(false); navigate("/friends"); }}
              className="flex flex-col items-center gap-2 p-4 rounded-xl bg-[#2e1a0a] border-2 border-orange-500/40 hover:border-orange-400 hover:bg-[#3b220f] transition-all shadow-[0_0_14px_rgba(249,115,22,0.25),inset_0_0_14px_rgba(249,115,22,0.08)]"
              data-testid="nav-sqawk-friends">
              <UserRound className="w-6 h-6 text-[#fdba74]" />
              <span className="text-white text-sm font-medium">sQawk Friends</span>
            </button>
            
            {/* sQawk Themes */}
            <button onClick={() => { setShowMobileMenu(false); navigate("/communities?tab=trends"); }}
              className="flex flex-col items-center gap-2 p-4 rounded-xl bg-[#2e1a0a] border-2 border-orange-500/40 hover:border-orange-400 hover:bg-[#3b220f] transition-all shadow-[0_0_14px_rgba(249,115,22,0.25),inset_0_0_14px_rgba(249,115,22,0.08)]">
              <Tag className="w-6 h-6 text-[#fdba74]" />
              <span className="text-white text-sm font-medium">sQawk Themes</span>
            </button>
            
            {/* sQawk Events — moved to Communities */}
            <button onClick={() => { setShowMobileMenu(false); navigate("/communities?tab=events"); }}
              className="flex flex-col items-center gap-2 p-4 rounded-xl bg-[#2e1a0a] border-2 border-orange-500/40 hover:border-orange-400 hover:bg-[#3b220f] transition-all shadow-[0_0_14px_rgba(249,115,22,0.25),inset_0_0_14px_rgba(249,115,22,0.08)]"
              data-testid="global-mobile-sqawk-events-btn">
              <Calendar className="w-6 h-6 text-[#fdba74]" />
              <span className="text-white text-sm font-medium">sQawk Events</span>
            </button>
            
            {/* === EXPLORE Section — Light Blue === */}
            <div className="col-span-2 flex items-center gap-2 mt-2">
              <Search className="w-4 h-4 text-[#7dd3fc]" />
              <span className="text-[#7dd3fc] text-xs font-semibold uppercase tracking-wider">Explore</span>
              <div className="flex-1 h-px bg-gradient-to-r from-sky-400/50 to-transparent" />
            </div>
            
            {/* 100k Directory */}
            <button onClick={() => { setShowMobileMenu(false); navigate("/business-hub?tab=directory"); }}
              className="flex flex-col items-center gap-2 p-4 rounded-xl bg-[#0a1e3d] border-2 border-sky-500/40 hover:border-sky-400 hover:bg-[#0f2850] transition-all shadow-[0_0_14px_rgba(14,165,233,0.25),inset_0_0_14px_rgba(14,165,233,0.08)]">
              <Globe className="w-6 h-6 text-[#7dd3fc]" />
              <span className="text-white text-sm font-medium text-center">100k Directory</span>
            </button>
            
            {/* Share & Referrals */}
            <button onClick={() => { setShowMobileMenu(false); navigate("/referrals"); }}
              className="flex flex-col items-center gap-2 p-4 rounded-xl bg-[#0a1e3d] border-2 border-sky-500/40 hover:border-sky-400 hover:bg-[#0f2850] transition-all shadow-[0_0_14px_rgba(14,165,233,0.25),inset_0_0_14px_rgba(14,165,233,0.08)]"
              data-testid="global-mobile-share-referrals-btn">
              <div className="flex items-center gap-1.5">
                <Share2 className="w-5 h-5 text-[#7dd3fc]" />
                <Gift className="w-5 h-5 text-[#fcd34d]" />
              </div>
              <span className="text-white text-sm font-medium text-center leading-tight">Share &<br/>Referrals</span>
            </button>
            
            {/* sQawk Jobs */}
            <button onClick={() => { setShowMobileMenu(false); navigate("/business-hub?tab=hiring"); setTimeout(() => { const el = document.querySelector('[data-testid="tab-hiring"]'); if (el) el.click(); }, 500); }}
              className="flex flex-col items-center gap-2 p-4 rounded-xl bg-[#0a1e3d] border-2 border-sky-500/40 hover:border-sky-400 hover:bg-[#0f2850] transition-all shadow-[0_0_14px_rgba(14,165,233,0.25),inset_0_0_14px_rgba(14,165,233,0.08)]">
              <Briefcase className="w-6 h-6 text-[#7dd3fc]" />
              <span className="text-white text-sm font-medium">sQawk Jobs</span>
            </button>
            
            {/* sQawk Skills */}
            <button onClick={() => { setShowMobileMenu(false); navigate("/sqawker-skills"); }}
              className="flex flex-col items-center gap-2 p-4 rounded-xl bg-[#0a1e3d] border-2 border-sky-500/40 hover:border-sky-400 hover:bg-[#0f2850] transition-all shadow-[0_0_14px_rgba(14,165,233,0.25),inset_0_0_14px_rgba(14,165,233,0.08)]">
              <Award className="w-6 h-6 text-[#7dd3fc]" />
              <span className="text-white text-sm font-medium">sQawk Skills</span>
            </button>
            
            {/* Business Hub */}
            <button onClick={() => { setShowMobileMenu(false); navigate("/business-hub"); window.scrollTo(0, 0); }}
              className="flex flex-col items-center gap-2 p-4 rounded-xl bg-[#0a1e3d] border-2 border-sky-500/40 hover:border-sky-400 hover:bg-[#0f2850] transition-all shadow-[0_0_14px_rgba(14,165,233,0.25),inset_0_0_14px_rgba(14,165,233,0.08)]">
              <Building className="w-6 h-6 text-[#7dd3fc]" />
              <span className="text-white text-sm font-medium">Business Hub</span>
            </button>
            
            {/* sQawk AI */}
            <button onClick={() => { setShowMobileMenu(false); navigate("/trending"); }}
              className="flex flex-col items-center gap-2 p-4 rounded-xl bg-[#0a1e3d] border-2 border-sky-500/40 hover:border-sky-400 hover:bg-[#0f2850] transition-all shadow-[0_0_14px_rgba(14,165,233,0.25),inset_0_0_14px_rgba(14,165,233,0.08)]">
              <Bot className="w-6 h-6 text-[#7dd3fc]" style={{ filter: "drop-shadow(0 0 8px rgba(34,211,238,0.5))" }} />
              <span className="text-white text-sm font-medium">sQawk AI</span>
            </button>
            
            {/* Non-Profits */}
            <button onClick={() => { setShowMobileMenu(false); navigate("/nonprofits"); }}
              className="flex flex-col items-center gap-2 p-4 rounded-xl bg-[#0a1e3d] border-2 border-sky-500/40 hover:border-sky-400 hover:bg-[#0f2850] transition-all shadow-[0_0_14px_rgba(14,165,233,0.25),inset_0_0_14px_rgba(14,165,233,0.08)]" data-testid="nav-nonprofits">
              <Heart className="w-6 h-6 text-[#7dd3fc]" />
              <span className="text-white text-sm font-medium">Non-Profits</span>
            </button>
            
            {/* My Schedule */}
            <button onClick={() => { setShowMobileMenu(false); navigate("/my-schedule"); }}
              className="flex flex-col items-center gap-2 p-4 rounded-xl bg-[#0a1e3d] border-2 border-sky-500/40 hover:border-sky-400 hover:bg-[#0f2850] transition-all shadow-[0_0_14px_rgba(14,165,233,0.25),inset_0_0_14px_rgba(14,165,233,0.08)]">
              <CalendarClock className="w-6 h-6 text-[#7dd3fc]" />
              <span className="text-white text-sm font-medium">My Schedule</span>
            </button>
            
            {/* === ACCOUNT Section — Yellow === */}
            <div className="col-span-2 flex items-center gap-2 mt-2">
              <User className="w-4 h-4 text-[#fde047]" />
              <span className="text-[#fde047] text-xs font-semibold uppercase tracking-wider">Account</span>
              <div className="flex-1 h-px bg-gradient-to-r from-yellow-400/50 to-transparent" />
            </div>
            
            {/* Profile */}
            <button onClick={() => { setShowMobileMenu(false); navigate(`/profile/${user?.username}`); }}
              className="flex flex-col items-center gap-2 p-4 rounded-xl bg-[#2e2a0a] border-2 border-yellow-500/40 hover:border-yellow-400 hover:bg-[#3b340f] transition-all shadow-[0_0_14px_rgba(250,204,21,0.25),inset_0_0_14px_rgba(250,204,21,0.08)]">
              <User className="w-6 h-6 text-[#fde047]" />
              <span className="text-white text-sm font-medium">Profile</span>
            </button>
            
            {/* Settings */}
            <button onClick={() => { setShowMobileMenu(false); navigate("/settings"); }}
              className="flex flex-col items-center gap-2 p-4 rounded-xl bg-[#2e2a0a] border-2 border-yellow-500/40 hover:border-yellow-400 hover:bg-[#3b340f] transition-all shadow-[0_0_14px_rgba(250,204,21,0.25),inset_0_0_14px_rgba(250,204,21,0.08)]">
              <Settings className="w-6 h-6 text-[#fde047]" />
              <span className="text-white text-sm font-medium">Settings</span>
            </button>
            
            {/* Admin (if creator) */}
            {user?.is_creator && (
              <button onClick={() => { setShowMobileMenu(false); navigate("/admin"); }}
                className="flex flex-col items-center gap-2 p-4 rounded-xl bg-[#2e2a0a] border-2 border-yellow-500/40 hover:border-yellow-400 hover:bg-[#3b340f] transition-all shadow-[0_0_14px_rgba(250,204,21,0.25),inset_0_0_14px_rgba(250,204,21,0.08)] col-span-2">
                <Shield className="w-6 h-6 text-[#fde047]" />
                <span className="text-white text-sm font-medium">Admin Dashboard</span>
              </button>
            )}
            
            {/* Theme Toggle + Logout — side by side */}
            <button onClick={() => { toggleTheme(); }}
              className={`flex flex-col items-center gap-2 p-4 rounded-xl border transition-all ${
                theme === 'light' 
                  ? 'bg-slate-100 border-slate-300 hover:border-slate-400 hover:bg-slate-200' 
                  : 'bg-slate-800/50 border-slate-700/50 hover:border-slate-600 hover:bg-slate-800'
              }`}
              data-testid="theme-toggle-btn"
            >
              {theme === 'dark' ? <Sun className="w-6 h-6 text-amber-400" /> : <Moon className="w-6 h-6 text-indigo-500" />}
              <span className="text-xs font-medium">{theme === 'dark' ? 'Light Mode' : 'Dark Mode'}</span>
            </button>
            
            <button onClick={() => { setShowMobileMenu(false); onLogout(); }}
              className="flex flex-col items-center gap-2 p-4 rounded-xl bg-[#3b0a0a]/80 border border-[#ef4444]/40 hover:border-[#f87171] hover:bg-[#3b0a0a] transition-all shadow-[0_0_12px_rgba(239,68,68,0.2)] hover:shadow-[0_0_20px_rgba(239,68,68,0.35)]"
              data-testid="mobile-logout-btn"
            >
              <LogOut className="w-6 h-6 text-[#fca5a5]" />
              <span className="text-[#fca5a5] text-xs font-medium">Logout</span>
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default GlobalMobileNav;
